"use client";

import { createContext, useContext, useEffect, useMemo, useRef } from "react";
import type { ChannelSummary } from "@golf-district/service";
import { api } from "~/utils/api";
import { CHANNEL } from "@golf-district/shared";
import { useSession } from "@golf-district/auth/nextjs-exports";

function _ct(): string {
  if (typeof window === 'undefined') return '?ms';
  const t0 = (window as any).__checkoutT0 as number | undefined;
  return t0 !== undefined ? `+${(performance.now() - t0).toFixed(0)}ms` : 'no-t0';
}

interface ChannelContextValue {
    channel: ChannelSummary | null;
    isLoading: boolean;
}

const ChannelContext = createContext<ChannelContextValue | null>(null);

export const ChannelWrapper = ({ children }: { children: React.ReactNode }) => {
    const fetchT0 = useRef<number>(performance.now());
    const session = useSession();
    const isPhoneGuest = !!session?.data?.user?.isPhoneGuest;
    // channelId is embedded in the JWT at login time — read it synchronously from session
    const sessionChannelId = (session?.data?.user as any)?.channelId as string | undefined;

    // Fast path: channel ID available from session JWT — construct the object without an API call
    const channelFromSession = useMemo((): ChannelSummary | null => {
        if (!sessionChannelId) return null;
        return {
            id: sessionChannelId,
            nid: isPhoneGuest ? CHANNEL.PHONE : CHANNEL.WHITE_LABEL,
            name: isPhoneGuest ? "AI Phone" : "White Label",
        };
    }, [sessionChannelId, isPhoneGuest]);

    // Fallback: fetch from API for sessions created before this change (no channelId in JWT yet)
    const { data: fetchedData, isLoading } = api.searchRouter.getChannels.useQuery(
        {},
        { enabled: !channelFromSession && session.status !== "loading" }
    );
    const channelFromApi = useMemo((): ChannelSummary | null => {
        if (!fetchedData) return null;
        if (isPhoneGuest) {
            return fetchedData.find((c) => (c.nid as CHANNEL) === CHANNEL.PHONE) ?? null;
        }
        return fetchedData.find((c) => (c.nid as CHANNEL) === CHANNEL.WHITE_LABEL) ?? null;
    }, [fetchedData, isPhoneGuest]);

    const channel = channelFromSession ?? channelFromApi;

    useEffect(() => {
        if (channel) {
            const took = (performance.now() - fetchT0.current).toFixed(0);
            const source = channelFromSession ? "session" : "api";
            console.log(`[CHECKOUT:CTX]  Channel resolved | took=${took}ms | source=${source} | id=${channel.id} | name=${channel.name} | nid=${channel.nid} | ${_ct()}`);
            console.log(`[CHECKOUT:CTX]  INFO — getTeeTimeById query is now ENABLED | ${_ct()}`);
        }
    }, [channel?.id]);

    const value = useMemo(
        () => ({
            channel,
            isLoading: !channelFromSession && isLoading,
        }),
        [channel, isLoading, channelFromSession]
    );
    return (
        <ChannelContext.Provider value={value}>
            {children}
        </ChannelContext.Provider>
    );
};

export const useChannel = (): ChannelContextValue => {
    const ctx = useContext(ChannelContext);
    if (!ctx) {
        throw new Error("useChannel must be used within ChannelProvider");
    }
    return ctx;
};
